import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import type { 
  StepHistoryElasticResponse, 
  StepHistoryElasticData, 
  StepHistoryElasticCountResponse,
  StepHistoryElasticEnumsResponse
} from './stephistoryElastic.types';

// GET step history elastic enums (status options)
export async function fetchStepHistoryElasticEnums({
  clientCode,
  firstResult = -1,
  maxResult = 0,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
}): Promise<string[]> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOBS.STEP_HISTORY_ELASTIC_ENUMS);
  const params: Record<string, any> = {
    firstResult,
    maxResult,
  };
  
  const response: StepHistoryElasticEnumsResponse = await api.get<StepHistoryElasticEnumsResponse>(endpoint, params);
  
  // Response format: [["SUCCESS","FAILURE"]]
  // Extract the first array if it exists
  if (response && response.length > 0 && Array.isArray(response[0])) {
    return response[0];
  }
  return [];
}

// GET step history elastic list
export async function fetchStepHistoryElastic({
  clientCode,
  firstResult = 0,
  maxResult = 10,
  stepId,
  clientId,
  jobId,
  jobSearch,
  jobDetailId,
  request,
  response,
  searchCriteria,
  status,
  lastUpdateDateFrom,
  lastUpdateDateTo,
  searched = true,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  stepId?: number | string;
  clientId?: number;
  jobId?: number;
  jobSearch?: string;
  jobDetailId?: number;
  request?: string;
  response?: string;
  searchCriteria?: string;
  status?: string;
  lastUpdateDateFrom?: string;
  lastUpdateDateTo?: string;
  searched?: boolean;
}): Promise<StepHistoryElasticData[]> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOBS.STEP_HISTORY_ELASTIC);
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    searched,
  };

  if (stepId !== undefined && stepId !== null && stepId !== '') {
    params.stepId = stepId;
  }
  if (clientId !== undefined && clientId !== null) {
    params.clientId = clientId;
  }
  if (jobId !== undefined && jobId !== null) {
    params.jobId = jobId;
  }
  if (jobSearch !== undefined && jobSearch !== null && jobSearch !== '') {
    params.jobSearch = jobSearch;
  }
  if (jobDetailId !== undefined && jobDetailId !== null) {
    params.jobDetailId = jobDetailId;
  }
  if (request !== undefined && request !== null && request !== '') {
    params.request = request;
  }
  if (response !== undefined && response !== null && response !== '') {
    params.response = response;
  }
  if (searchCriteria !== undefined && searchCriteria !== null && searchCriteria !== '') {
    params.searchCriteria = searchCriteria;
  }
  if (status !== undefined && status !== null && status !== '') {
    params.status = status;
  }
  if (lastUpdateDateFrom !== undefined && lastUpdateDateFrom !== null && lastUpdateDateFrom !== '') {
    params.lastUpdateDateFrom = lastUpdateDateFrom;
  }
  if (lastUpdateDateTo !== undefined && lastUpdateDateTo !== null && lastUpdateDateTo !== '') {
    params.lastUpdateDateTo = lastUpdateDateTo;
  }

  const responseData: StepHistoryElasticResponse = await api.get<StepHistoryElasticResponse>(endpoint, params);
  
  // Convert stepHistoryDTOs to StepHistoryElasticData objects
  if (responseData?.result?.stepHistoryDTOs) {
    return responseData.result.stepHistoryDTOs.map((dto) => ({
      id: dto.id || '',
      stepId: dto.stepId,
      lastUpdateDate: dto.lastUpdateDate,
      response: dto.response,
      status: dto.stepHistoryStatus,
      header: dto.header,
      request: dto.request,
      configObjectJson: dto.configObjectJson,
      clientId: dto.clientId,
      jobId: dto.jobId,
    }));
  }
  return [];
}

// GET step history elastic count
export async function fetchStepHistoryElasticCount({
  clientCode,
  stepId,
  clientId,
  jobId,
  jobSearch,
  jobDetailId,
  request,
  response,
  searchCriteria,
  status,
  lastUpdateDateFrom,
  lastUpdateDateTo,
  searched = true,
}: {
  clientCode: string;
  stepId?: number | string;
  clientId?: number;
  jobId?: number;
  jobSearch?: string;
  jobDetailId?: number;
  request?: string;
  response?: string;
  searchCriteria?: string;
  status?: string;
  lastUpdateDateFrom?: string;
  lastUpdateDateTo?: string;
  searched?: boolean;
}): Promise<number> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.JOBS.STEP_HISTORY_ELASTIC_COUNT);
  const params: Record<string, any> = {
    searched,
  };

  if (stepId !== undefined && stepId !== null && stepId !== '') {
    params.stepId = stepId;
  }
  if (clientId !== undefined && clientId !== null) {
    params.clientId = clientId;
  }
  if (jobId !== undefined && jobId !== null) {
    params.jobId = jobId;
  }
  if (jobSearch !== undefined && jobSearch !== null && jobSearch !== '') {
    params.jobSearch = jobSearch;
  }
  if (jobDetailId !== undefined && jobDetailId !== null) {
    params.jobDetailId = jobDetailId;
  }
  if (request !== undefined && request !== null && request !== '') {
    params.request = request;
  }
  if (response !== undefined && response !== null && response !== '') {
    params.response = response;
  }
  if (searchCriteria !== undefined && searchCriteria !== null && searchCriteria !== '') {
    params.searchCriteria = searchCriteria;
  }
  if (status !== undefined && status !== null && status !== '') {
    params.status = status;
  }
  if (lastUpdateDateFrom !== undefined && lastUpdateDateFrom !== null && lastUpdateDateFrom !== '') {
    params.lastUpdateDateFrom = lastUpdateDateFrom;
  }
  if (lastUpdateDateTo !== undefined && lastUpdateDateTo !== null && lastUpdateDateTo !== '') {
    params.lastUpdateDateTo = lastUpdateDateTo;
  }

  try {
    const response = await api.get<any>(endpoint, params);
    
    // Handle different response formats
    if (typeof response === 'number') {
      return response;
    } else if (response && typeof response === 'object' && 'count' in response) {
      const countValue = (response as any).count;
      return typeof countValue === 'number' ? countValue : 0;
    } else if (typeof response === 'string') {
      const parts = response.trim().split(' ');
      if (parts.length > 0) {
        const countValue = parseInt(parts[parts.length - 1], 10);
        return isNaN(countValue) ? 0 : countValue;
      }
      return 0;
    }
    
    logger.warn('Unexpected count response format:', response);
    return 0;
  } catch (error) {
    logger.error('Error fetching step history elastic count:', error);
    return 0;
  }
}

